import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { todayISO } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import DataTable from "@/components/DataTable";
import ExportPreview from "@/components/ExportPreview";
import { F } from "@/pages/Employees";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, LogIn, AlertTriangle, Eye, Printer } from "lucide-react";
import { toast } from "sonner";

export default function ReportAttendanceRealtime() {
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("all");
  const [tanggal, setTanggal] = useState(todayISO());
  const [rows, setRows] = useState([]);
  const [ringkasan, setRingkasan] = useState({ total: 0, hadir: 0, telat: 0 });
  const [preview, setPreview] = useState(false);

  useEffect(() => { api.get("/projects").then((r) => setProjects(r.data)).catch(() => {}); }, []);

  const load = async () => {
    try {
      const q = `tanggal=${tanggal}${projectId && projectId !== "all" ? `&project_id=${projectId}` : ""}`;
      const r = await api.get(`/reports/attendance-daily?${q}`);
      setRows(r.data.rows);
      setRingkasan(r.data.ringkasan);
    } catch (e) { toast.error(apiErr(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tanggal, projectId]);

  const dlUrl = (fmt) => {
    const query = `tanggal=${tanggal}${projectId && projectId !== "all" ? `&project_id=${projectId}` : ""}`;
    return { url: `/reports/attendance-daily/${fmt}?${query}`, filename: `rekap_absensi_${tanggal}.${fmt}` };
  };

  const columns = [
    { key: "employee_nama", label: "Pegawai", sortable: true, render: (r) => <div><span className="font-semibold text-slate-800">{r.employee_nama}</span><div className="text-xs text-slate-400">{r.kode_pegawai}</div></div> },
    { key: "project_nama", label: "Proyek", sortable: true },
    { key: "status", label: "Status", render: (r) => <span className="text-xs font-semibold">{r.status}</span> },
    { key: "jam_masuk", label: "Jam Datang", render: (r) => r.jam_masuk || <span className="text-slate-300">—</span> },
    { key: "jam_pulang", label: "Jam Pulang", render: (r) => r.jam_pulang || <span className="text-slate-300">—</span> },
    { key: "telat", label: "Telat", sortable: true, sortValue: (r) => r.menit_telat, render: (r) => r.telat ? <span className="rounded bg-rose-100 px-2 py-0.5 text-xs font-bold text-rose-700" data-testid={`rt-telat-${r.id}`}>TELAT {r.menit_telat}m</span> : <span className="text-emerald-600 text-xs font-semibold">Tepat</span> },
    { key: "jam_lembur", label: "Lembur (jam)", align: "right", render: (r) => r.jam_lembur || 0 },
  ];

  return (
    <div className="space-y-6">
      <PageHeader title="Rekap Absensi (Realtime)" subtitle="Pratinjau di layar, lalu unduh PDF / Excel">
        {rows.length > 0 && <>
          <Button variant="outline" onClick={() => setPreview(true)} data-testid="rt-preview"><Eye className="h-4 w-4 mr-1" /> Pratinjau & Unduh</Button>
          <Button variant="outline" onClick={() => window.print()} data-testid="rt-print"><Printer className="h-4 w-4 mr-1" /> Print</Button>
        </>}
      </PageHeader>
      <Card className="p-4 no-print">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
          <F label="Tanggal"><Input type="date" value={tanggal} data-testid="rt-date" onChange={(e) => setTanggal(e.target.value)} /></F>
          <F label="Proyek">
            <Select value={projectId} onValueChange={setProjectId}>
              <SelectTrigger data-testid="rt-project"><SelectValue placeholder="Semua proyek" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Proyek</SelectItem>
                {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.nama}</SelectItem>)}
              </SelectContent>
            </Select>
          </F>
        </div>
      </Card>

      <div className="grid grid-cols-3 gap-3">
        <Stat icon={Users} label="Total Absen" value={ringkasan.total} tone="slate" />
        <Stat icon={LogIn} label="Hadir" value={ringkasan.hadir} tone="emerald" />
        <Stat icon={AlertTriangle} label="Telat" value={ringkasan.telat} tone="rose" />
      </div>

      <DataTable columns={columns} data={rows} searchKeys={["employee_nama", "project_nama", "kode_pegawai"]} pageSize={15} testid="rt-table"
        toolbar={<span className="text-sm text-slate-500">{rows.length} record</span>} />

      <ExportPreview open={preview} onClose={() => setPreview(false)} title="Pratinjau Rekap Absensi"
        pdfUrl={dlUrl("pdf").url}
        onPrint={() => window.print()}
        items={[
          { label: "Unduh PDF", ...dlUrl("pdf") },
          { label: "Unduh Excel", ...dlUrl("xlsx") },
        ]} />
    </div>
  );
}

const TONE = {
  slate: "bg-slate-100 text-slate-700", emerald: "bg-emerald-100 text-emerald-700", rose: "bg-rose-100 text-rose-700",
};
const Stat = ({ icon: Icon, label, value, tone }) => (
  <Card className="p-4 flex items-center gap-3" data-testid={`rt-stat-${label}`}>
    <span className={`flex h-10 w-10 items-center justify-center rounded-lg ${TONE[tone]}`}><Icon className="h-5 w-5" /></span>
    <div>
      <div className="text-2xl font-black text-slate-800">{value}</div>
      <div className="text-xs text-slate-500">{label}</div>
    </div>
  </Card>
);
